import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Cloud, Code, Zap, Shield, ChevronRight, ArrowRight, Check, Star } from 'lucide-react';
import SEO from '../components/SEO';
import QivratechLogo from '../components/QivratechLogo';

const SolutionsPage = () => {
  const [activeService, setActiveService] = useState(0);
  const [hoveredFeature, setHoveredFeature] = useState(null);

  useEffect(() => {
    // Auto-rotate through services
    const interval = setInterval(() => {
      setActiveService((prev) => (prev + 1) % solutions.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const solutions = [
    {
      id: 'ai-ml',
      icon: <Brain className="w-16 h-16" />,
      title: "AI & Machine Learning Solutions",
      subtitle: "Intelligent Automation for Modern Business",
      description: "Transform your business with cutting-edge AI technologies that deliver measurable results. Our ML solutions adapt, learn, and optimize your operations in real-time.",
      features: [
        "Predictive Analytics & Forecasting",
        "Natural Language Processing (NLP)",
        "Computer Vision & Image Recognition",
        "Deep Learning Neural Networks",
        "Recommendation Systems",
        "Anomaly Detection & Fraud Prevention",
        "Chatbots & Virtual Assistants",
        "Process Automation"
      ],
      technologies: ["TensorFlow", "PyTorch", "Scikit-learn", "OpenCV", "Hugging Face", "AWS SageMaker", "Azure ML", "Google AI"],
      useCases: [
        { title: "Customer Behavior Analysis", description: "Predict customer preferences and optimize marketing campaigns" },
        { title: "Predictive Maintenance", description: "Reduce downtime with AI-powered equipment monitoring" },
        { title: "Intelligent Document Processing", description: "Automate data extraction from unstructured documents" }
      ],
      roi: "40-60% increase in operational efficiency",
      color: "from-cyan-400 to-blue-500",
      bgGradient: "from-cyan-500/10 to-blue-600/10"
    },
    {
      id: 'cloud',
      icon: <Cloud className="w-16 h-16" />,
      title: "Cloud & Digital Transformation",
      subtitle: "Scalable Infrastructure for Growth",
      description: "Modernize your IT infrastructure with cloud-native solutions that scale with your business. We ensure seamless migration and optimization for maximum performance.",
      features: [
        "Cloud Migration & Strategy",
        "Kubernetes & Container Orchestration",
        "Microservices Architecture",
        "DevOps & CI/CD Pipelines",
        "Infrastructure as Code (IaC)",
        "Serverless Computing",
        "Multi-Cloud & Hybrid Solutions",
        "Cloud Security & Compliance"
      ],
      technologies: ["AWS", "Azure", "Google Cloud", "Kubernetes", "Docker", "Terraform", "Jenkins", "GitLab CI"],
      useCases: [
        { title: "Legacy System Modernization", description: "Transform monolithic applications into cloud-native microservices" },
        { title: "Auto-Scaling Infrastructure", description: "Automatically adjust resources based on demand" },
        { title: "Disaster Recovery", description: "Ensure business continuity with cloud-based backup solutions" }
      ],
      roi: "30-50% reduction in infrastructure costs",
      color: "from-purple-400 to-pink-500",
      bgGradient: "from-purple-500/10 to-pink-600/10"
    },
    {
      id: 'software',
      icon: <Code className="w-16 h-16" />,
      title: "Custom Software & SaaS Development",
      subtitle: "Tailored Solutions for Your Business",
      description: "Build powerful, scalable applications that perfectly fit your business needs. From web platforms to mobile apps, we create solutions that drive growth.",
      features: [
        "Full-Stack Web Development",
        "Mobile App Development (iOS/Android)",
        "SaaS Platform Development",
        "API Development & Integration",
        "Database Design & Optimization",
        "Real-time Applications",
        "E-commerce Solutions",
        "Progressive Web Apps (PWAs)"
      ],
      technologies: ["React", "Node.js", "Python", "React Native", "PostgreSQL", "MongoDB", "Redis", "GraphQL"],
      useCases: [
        { title: "Enterprise SaaS Platform", description: "Build scalable multi-tenant applications for your market" },
        { title: "Customer Portal", description: "Create seamless experiences for your clients" },
        { title: "Internal Tools", description: "Develop custom solutions for operational efficiency" }
      ],
      roi: "25-40% improvement in business processes",
      color: "from-green-400 to-teal-500",
      bgGradient: "from-green-500/10 to-teal-600/10"
    },
    {
      id: 'consulting',
      icon: <Zap className="w-16 h-16" />,
      title: "Strategic IT Consulting",
      subtitle: "Expert Guidance for Digital Success",
      description: "Navigate complex technology decisions with confidence. Our strategic consulting helps you make informed choices that align with your business objectives.",
      features: [
        "Digital Transformation Strategy",
        "Technology Roadmap Planning",
        "Enterprise Architecture Design",
        "IT Process Optimization",
        "Vendor Selection & Management",
        "Risk Assessment & Mitigation",
        "Performance Audits",
        "Change Management"
      ],
      technologies: ["TOGAF", "ITIL", "Agile", "Scrum", "COBIT", "Enterprise Tools", "Analytics Platforms"],
      useCases: [
        { title: "Digital Strategy Development", description: "Create comprehensive roadmaps for technology adoption" },
        { title: "System Integration", description: "Connect disparate systems for unified operations" },
        { title: "Process Optimization", description: "Streamline workflows for maximum efficiency" }
      ],
      roi: "20-35% increase in operational productivity",
      color: "from-yellow-400 to-orange-500",
      bgGradient: "from-yellow-500/10 to-orange-600/10"
    },
    {
      id: 'security',
      icon: <Shield className="w-16 h-16" />,
      title: "Cybersecurity & Managed IT",
      subtitle: "Comprehensive Protection & Management",
      description: "Protect your digital assets with enterprise-grade security solutions. Our managed services ensure your systems are always secure, monitored, and optimized.",
      features: [
        "Security Audits & Assessments",
        "Penetration Testing",
        "24/7 Security Monitoring",
        "Incident Response & Recovery",
        "Compliance Management",
        "Identity & Access Management",
        "Network Security",
        "Managed IT Services"
      ],
      technologies: ["SIEM Tools", "Penetration Testing", "Firewalls", "Identity Management", "Compliance Tools"],
      useCases: [
        { title: "Security Compliance", description: "Ensure compliance with industry standards (SOC 2, HIPAA, PCI DSS)" },
        { title: "Threat Detection", description: "Proactive monitoring and response to security threats" },
        { title: "Managed Services", description: "Complete IT infrastructure management and support" }
      ],
      roi: "60-80% reduction in security incidents",
      color: "from-red-400 to-pink-500",
      bgGradient: "from-red-500/10 to-pink-600/10"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      <SEO
        title="AI & IT Solutions - Qivra Technologies | Comprehensive Technology Services"
        description="Comprehensive AI, machine learning, cloud computing, and software development solutions. Intelligence unleashed for enterprise digital transformation and growth."
        keywords="AI solutions, machine learning services, cloud computing, software development, digital transformation, enterprise technology, custom solutions, Qivra Technologies"
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black">
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
                radial-gradient(circle, rgba(14, 165, 233, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '100% 100%, 100% 100%, 60px 60px'
            }}
          />
          
          {/* Floating Logo Elements */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Top Area */}
            <motion.div
              className="absolute top-18 left-14 opacity-7"
              animate={{
                y: [0, -27, 0],
                x: [0, 15, 0],
                rotate: [0, 5, 0]
              }}
              transition={{
                duration: 11,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute top-40 right-18 opacity-6"
              animate={{
                y: [0, -19, 0],
                x: [0, -11, 0],
                rotate: [0, -4, 0]
              }}
              transition={{
                duration: 13,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 3
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            {/* Middle Area */}
            <motion.div
              className="absolute top-1/2 left-4 opacity-4"
              animate={{
                y: [0, -33, 0],
                x: [0, 25, 0],
                rotate: [0, 8, 0]
              }}
              transition={{
                duration: 16,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 4
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            {/* Bottom Area */}
            <motion.div
              className="absolute bottom-30 left-18 opacity-8"
              animate={{
                y: [0, -29, 0],
                x: [0, 21, 0],
                rotate: [0, 8, 0]
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 6
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute bottom-18 right-14 opacity-9"
              animate={{
                y: [0, -33, 0],
                x: [0, -17, 0],
                rotate: [0, -6, 0]
              }}
              transition={{
                duration: 9,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 8
              }}
            >
              <QivratechLogo variant="mark" size="small" theme="dark" />
            </motion.div>
          </div>
        </div>
        
        <motion.div 
          className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Comprehensive Solutions</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 leading-tight tracking-wide">
              <span className="text-white">AI & IT</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent font-light">
                Solutions
              </span>
            </h1>
            
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide mb-12">
              Intelligence unleashed through comprehensive technology solutions that accelerate growth and competitive advantage
            </p>
          </motion.div>

          {/* Solution Navigator */}
          <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4 mb-12">
            {solutions.map((solution, idx) => (
              <motion.button
                key={idx}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveService(idx)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold transition-all ${
                  activeService === idx
                    ? 'bg-gradient-to-r from-cyan-400 to-purple-600 text-white shadow-lg shadow-cyan-400/50'
                    : 'bg-gray-800/50 text-gray-300 hover:text-cyan-400 border border-gray-700'
                }`}
              >
                <div className="w-6 h-6">{React.cloneElement(solution.icon, { className: "w-6 h-6" })}</div>
                <span className="hidden sm:inline">{solution.title.split(' ')[0]}</span>
              </motion.button>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Active Solution Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            key={activeService}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={`bg-gradient-to-br ${solutions[activeService].bgGradient} rounded-3xl p-8 md:p-12 border border-cyan-400/20`}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className={`text-cyan-400 mb-6`}>
                  {solutions[activeService].icon}
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                  {solutions[activeService].title}
                </h2>
                <h3 className="text-xl text-cyan-400 mb-6">
                  {solutions[activeService].subtitle}
                </h3>
                <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                  {solutions[activeService].description}
                </p>
                
                <div className="bg-gradient-to-r from-cyan-500/20 to-purple-600/20 p-6 rounded-2xl border border-cyan-400/30 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <span className="font-bold text-white">Expected ROI</span>
                  </div>
                  <p className="text-cyan-400 font-bold text-lg">{solutions[activeService].roi}</p>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-bold text-white mb-6">Key Features & Capabilities</h4>
                <div className="grid gap-3 mb-8">
                  {solutions[activeService].features.map((feature, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-gray-700 hover:border-cyan-400/50 transition-all"
                    >
                      <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                <h4 className="text-xl font-bold text-white mb-4">Technologies We Use</h4>
                <div className="flex flex-wrap gap-2 mb-8">
                  {solutions[activeService].technologies.map((tech, idx) => (
                    <span
                      key={idx}
                      className="bg-gradient-to-r from-cyan-500/20 to-purple-600/20 text-cyan-300 px-3 py-1 rounded-full text-sm font-medium border border-cyan-400/30"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Real-World Applications
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              See how our {solutions[activeService].title.toLowerCase()} create tangible business value
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {solutions[activeService].useCases.map((useCase, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                onHoverStart={() => setHoveredFeature(idx)}
                onHoverEnd={() => setHoveredFeature(null)}
                className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl border border-gray-700 hover:border-cyan-400/50 transition-all"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-lg">{idx + 1}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{useCase.title}</h3>
                <p className="text-gray-400 leading-relaxed">{useCase.description}</p>
                
                <motion.div
                  initial={false}
                  animate={{ opacity: hoveredFeature === idx ? 1 : 0 }}
                  className="mt-4 pt-4 border-t border-gray-700"
                >
                  <span className="text-cyan-400 font-semibold flex items-center gap-2">
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-b from-gray-950 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Start Your Journey</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-extralight mb-8 leading-tight tracking-wide text-white">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-12 font-light leading-relaxed tracking-wide">
              Let's discuss how our solutions can accelerate your growth and drive measurable results
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/contact"
                className="group bg-gradient-to-r from-cyan-400/10 to-purple-600/10 border border-cyan-400/30 px-8 py-4 rounded-2xl font-light tracking-wide text-white hover:bg-gradient-to-r hover:from-cyan-400/20 hover:to-purple-600/20 hover:border-cyan-400/60 transition-all duration-300 flex items-center gap-2 justify-center backdrop-blur-sm"
              >
                <span>Talk to an Expert</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/projects"
                className="group bg-gradient-to-r from-purple-400/10 to-cyan-600/10 border border-purple-400/30 px-8 py-4 rounded-2xl font-light tracking-wide text-purple-300 hover:bg-gradient-to-r hover:from-purple-400/20 hover:to-cyan-600/20 hover:border-purple-400/60 hover:text-white transition-all duration-300 backdrop-blur-sm"
              >
                View Case Studies
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;